import { TDay } from '@src/ui/components/Admin/Shifts/ShiftEditDuplicate/utils/types'
import { addDays, format, isAfter, isBefore, isEqual, parse } from 'date-fns'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import {
  DATE_AND_TIME_FORMAT_STRING,
  DATE_FORMAT_STRING,
  TIME_FORMAT_STRING,
} from 'helpers/constants/constants'

/**
 * Format Datestamp (DS)
 * Helper function for formatting datestamp in format YYYY-MM-DD
 * This format should be used when sending dates in requests
 * @param date
 * @returns date in YYYY-MM-DD format
 */
export const formatDS = (date: Date) => {
  return format(date, DATE_FORMAT_STRING.yyyy_MM_dd)
}

/**
 * Helper function to check if a date is the same or before another date
 */
export const isSameOrBefore = (date1: Date, date2: Date) => {
  return isBefore(date1, date2) || isEqual(date1, date2)
}

/**
 * Helper function to check if a date is the same or after another date
 */
export const isSameOrAfter = (date1: Date, date2: Date) => {
  return isAfter(date1, date2) || isEqual(date1, date2)
}

/**
 * Helper function to check if two times are in the same day
 * @param time_start Start time
 * @param time_end End time
 * @returns True if the times are in the same day, false otherwise
 */
export const areTimesInTheSameDay = (time_start: string, time_end: string) => {
  const referenceDate = new Date()
  const startDate = parse(time_start, TIME_FORMAT_STRING.hh_mm_a, referenceDate)
  const endDate = parse(time_end, TIME_FORMAT_STRING.hh_mm_a, referenceDate)

  // If end time is before or equal to start time, the shift crosses midnight
  return endDate > startDate
}

/**
 * Helper function to get the final day of a shift
 * @param day_start Start day
 * @param hour_start Start hour
 * @param hour_end End hour
 * @returns The final day of the shift
 */
export const getFinalDay = (day_start: Date, hour_start: string, hour_end: string) => {
  return areTimesInTheSameDay(hour_start, hour_end)
    ? new Date(day_start)
    : addDays(new Date(day_start), 1)
}

const parseDateWithTime = (date: string, hour: string) => {
  const normalizedHour = format(
    parse(hour, TIME_FORMAT_STRING.h_mm_a, new Date()),
    TIME_FORMAT_STRING.h_mm_aaa
  )

  return parse(
    `${date} ${normalizedHour}`,
    DATE_AND_TIME_FORMAT_STRING.MM_dd_yyyy_h_mm_aaa,
    new Date()
  )
}

export const convertTimeAndHours = (
  date_start: string,
  date_end: string,
  hour_start: string,
  hour_end: string,
  timezone: string
) => {
  const startDateLocal = parseDateWithTime(date_start, hour_start)
  const endDateLocal = parseDateWithTime(date_end, hour_end)

  const startUtc = fromZonedTime(startDateLocal, timezone)
  const endUtc = fromZonedTime(endDateLocal, timezone)

  return [
    formatInTimeZone(startUtc, 'UTC', DATE_FORMAT_STRING.MM_dd_yyyy),
    formatInTimeZone(endUtc, 'UTC', DATE_FORMAT_STRING.MM_dd_yyyy),
    formatInTimeZone(startUtc, 'UTC', TIME_FORMAT_STRING.h_mm_aaa),
    formatInTimeZone(endUtc, 'UTC', TIME_FORMAT_STRING.h_mm_aaa),
  ]
}

export const convertToPropertyTimezone = (
  dateStart: string,
  hourStart: string,
  hourEnd: string,
  propertyTimezone: string
) => {
  // This is the date and time in the property timezone
  const combinedStartDate = `${dateStart} ${hourStart}`
  const combinedEndDate = `${dateStart} ${hourEnd}`

  const localStartDate = parse(combinedStartDate, 'MM/dd/yyyy hh:mm a', new Date())
  let localEndDate = parse(combinedEndDate, 'MM/dd/yyyy hh:mm a', new Date())
  if (isBefore(localEndDate, localStartDate)) {
    localEndDate = addDays(localEndDate, 1)
  }

  // Convert the string from property timezone to UTC
  const startDateUtc = fromZonedTime(localStartDate, propertyTimezone)

  const endDateUtc = fromZonedTime(localEndDate, propertyTimezone)

  const result = {
    date_start: formatInTimeZone(startDateUtc, 'UTC', DATE_FORMAT_STRING.MM_dd_yyyy),
    hour_start: formatInTimeZone(startDateUtc, 'UTC', TIME_FORMAT_STRING.h_mm_aaa),
    hour_end: formatInTimeZone(endDateUtc, 'UTC', TIME_FORMAT_STRING.h_mm_aaa),
  }

  return result
}

export const convertTime = (days: TDay[], timezone: string) => {
  const adjustedDays = days.map((day) => {
    const { hour_start, hour_end, date_start, date_end } = day

    const parsedStart = parseDateWithTime(date_start, hour_start)
    const parsedEnd = parseDateWithTime(date_end, hour_end)

    const startUtc = fromZonedTime(parsedStart, timezone)
    const endUtc = fromZonedTime(parsedEnd, timezone)

    return {
      ...day,
      date_start: formatInTimeZone(startUtc, 'UTC', DATE_FORMAT_STRING.MM_dd_yyyy),
      date_end: formatInTimeZone(endUtc, 'UTC', DATE_FORMAT_STRING.MM_dd_yyyy),
      day_start: startUtc,
      day_end: endUtc,
      hour_start: formatInTimeZone(startUtc, 'UTC', TIME_FORMAT_STRING.h_mm_aaa),
      hour_end: formatInTimeZone(endUtc, 'UTC', TIME_FORMAT_STRING.h_mm_aaa),
    }
  })

  return adjustedDays
}
